import { motion } from "framer-motion";
import { Minus, Plus, Snowflake, Sparkles } from "lucide-react";
import Reveal from "./Reveal";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const theme = {
  blue: { ring: "#0077B6", soft: "rgba(0,119,182,0.10)", text: "text-ocean", bg: "bg-ocean", shadow: "drop-shadow-[0_25px_35px_rgba(0,119,182,0.30)]" },
  pink: { ring: "#FF4D6D", soft: "rgba(255,77,109,0.10)", text: "text-watermelon", bg: "bg-watermelon", shadow: "drop-shadow-[0_25px_35px_rgba(255,77,109,0.30)]" },
  gold: { ring: "#F0A500", soft: "rgba(240,165,0,0.12)", text: "text-[#B8790B]", bg: "bg-[#F0A500]", shadow: "drop-shadow-[0_25px_35px_rgba(240,165,0,0.32)]" },
  green: { ring: "#8AC926", soft: "rgba(138,201,38,0.14)", text: "text-[#5B8A0F]", bg: "bg-leaf", shadow: "drop-shadow-[0_25px_35px_rgba(138,201,38,0.30)]" },
};

function QtyStepper({ qty, onChange, color }) {
  return (
    <div className="inline-flex items-center gap-3 rounded-full border-2 border-ink/10 bg-white p-1.5">
      <motion.button
        whileTap={{ scale: 0.85 }}
        onClick={() => onChange(Math.max(0, qty - 1))}
        className="grid h-9 w-9 place-items-center rounded-full bg-cream text-ink hover:bg-ink/5"
        data-testid={`qty-minus-${color}`}
        aria-label="decrease"
      >
        <Minus className="h-4 w-4" />
      </motion.button>
      <span className="w-6 text-center font-display text-lg font-bold" data-testid={`qty-value-${color}`}>{qty}</span>
      <motion.button
        whileTap={{ scale: 0.85 }}
        onClick={() => onChange(qty + 1)}
        className={`grid h-9 w-9 place-items-center rounded-full text-white ${theme[color].bg}`}
        data-testid={`qty-plus-${color}`}
        aria-label="increase"
      >
        <Plus className="h-4 w-4" />
      </motion.button>
    </div>
  );
}

function ProductCard({ p, qty, setQty, index }) {
  const t = theme[p.color] || theme.pink;
  return (
    <Reveal delay={index * 0.12}>
      <div
        className="relative flex flex-col overflow-hidden rounded-[2rem] border border-white/60 bg-white/70 p-7 sm:p-9 backdrop-blur-xl"
        style={{ boxShadow: `0 30px 60px -30px ${t.ring}55` }}
        data-testid={`product-card-${p.id}`}
      >
        <div className="wc-blob" style={{ background: t.ring, width: 260, height: 260, top: -80, right: -80, opacity: 0.18 }} />

        <div className="relative z-10 flex items-start justify-between gap-4">
          <div>
            <span className={`inline-block rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.15em] ${t.text}`} style={{ background: t.soft }}>
              {p.size}
            </span>
            <h3 className="mt-3 font-display text-3xl sm:text-4xl font-extrabold uppercase leading-none tracking-tight text-ink">
              {p.name}
            </h3>
            <p className="mt-1 font-script text-2xl text-ink/60">{p.tagline}</p>
          </div>
          {p.sold_out && (
            <span className="rounded-full bg-ink px-3 py-1 text-xs font-bold uppercase tracking-wide text-white" data-testid={`sold-out-${p.id}`}>
              Sold out
            </span>
          )}
        </div>

        <div className="relative z-10 my-6 flex items-center justify-center">
          <motion.img
            src={p.image}
            alt={p.name}
            className={`h-64 sm:h-72 w-auto ${t.shadow}`}
            animate={{ y: [0, -10, 0] }}
            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
          />
        </div>

        <div className="relative z-10 flex flex-wrap gap-2">
          {p.benefits.map((b) => (
            <span key={b} className="inline-flex items-center gap-1.5 rounded-full bg-cream px-3 py-1.5 text-xs font-semibold text-ink/80">
              <Sparkles className={`h-3.5 w-3.5 ${t.text}`} /> {b}
            </span>
          ))}
        </div>

        <div className="relative z-10 mt-5">
          <p className="text-[11px] font-semibold uppercase tracking-[0.15em] text-ink/40">Ingredients</p>
          <p className="mt-1 text-sm leading-relaxed text-ink/70">{p.ingredients.join(" · ")}</p>
        </div>

        <div className="relative z-10 mt-4 inline-flex items-center gap-2 text-xs font-medium text-ocean">
          <Snowflake className="h-4 w-4" /> Keep refrigerated · Made fresh
        </div>

        <div className="relative z-10 mt-6 flex items-center justify-between border-t border-ink/10 pt-6">
          <div>
            <span className="font-display text-4xl font-extrabold text-ink" data-testid={`price-${p.id}`}>${p.price.toFixed(2)}</span>
            <span className="text-sm text-ink/50"> / bottle</span>
          </div>
          {p.sold_out ? (
            <span className="rounded-full bg-ink/10 px-5 py-3 text-sm font-semibold text-ink/50">Unavailable</span>
          ) : (
            <QtyStepper qty={qty} onChange={(v) => setQty(p.id, v)} color={p.color} />
          )}
        </div>

        {!p.sold_out && (
          <button
            onClick={() => { if (qty === 0) setQty(p.id, 1); scrollTo("order"); }}
            data-testid={`add-order-${p.id}`}
            className={`relative z-10 mt-5 w-full rounded-full ${t.bg} py-4 text-base font-semibold text-white transition-transform duration-200 hover:scale-[1.02] active:scale-95`}
          >
            Add to order
          </button>
        )}
      </div>
    </Reveal>
  );
}

export default function Products({ products, quantities, setQty }) {
  const words = ["Zero", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
  const countWord = words[products.length] || `${products.length}`;
  return (
    <section id="products" className="relative mx-auto max-w-6xl px-5 sm:px-8 py-20 sm:py-28">
      <Reveal>
        <p className="font-script text-3xl text-watermelon">01 — the juices</p>
        <h2 className="mt-1 font-display text-4xl sm:text-6xl font-extrabold uppercase tracking-tighter text-ink">
          {countWord} flavors.<br />Zero shortcuts.
        </h2>
        <p className="mt-4 max-w-xl text-ink/60">
          Cold-pressed, all-natural, and packed with real fruit and sea moss. Pick your bottles and set your quantity.
        </p>
      </Reveal>

      <div className="mt-12 grid gap-8 md:grid-cols-2">
        {products.map((p, i) => (
          <ProductCard key={p.id} p={p} index={i} qty={quantities[p.id] || 0} setQty={setQty} />
        ))}
      </div>
    </section>
  );
}
